import { EyeIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  REMINDER_CONSTRAINTS,
  ReminderTimeSelect,
} from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import {
  MODAL_DIALOG_PREVIEW,
  MODAL_CREATE_SAMPLE_DIALOG,
} from '@/components/organisms/Modals/ModalConductor';
import { CreateSampleDialogFormValues } from '@/components/organisms/Modals/ModalCreateSampleDialog';
import { Button } from '@/components/ui/button';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogs from '@/hooks/useDialogs';
import { languageSupportsEnhance } from '@/redux/dialogs/helper';
import { popModal, pushModal } from '@/redux/modals/actions';
import { selectAccountId } from '@/redux/session/selectors';
import { Brain } from '@/shared/types/brain';
import { formatSecondsToReadable } from '@/util/dateUtils';

import { NodeOptionLabel } from './NodeOptionLabel';
import { FormReminder, isEmptyReminder, NodeOption, PreferencesForm } from './preferencesForm';

export const DEFAULT_TIMER_VALUES = {
  reminder_id: uuidv4(),
  reminder_seconds: null,
  node: null,
} as const;

interface ReminderMessageFieldProps {
  brain: Brain;
  brainId: string;
  canWrite: boolean;
}

export const ReminderMessageField = ({ brain, brainId, canWrite }: ReminderMessageFieldProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);

  const { detailedOptions, createDialog } = useDialogs(brainId);
  const { control, trigger } = useFormContext<PreferencesForm>();
  const { errors, isSubmitted } = useFormState({ control });

  const {
    fields: reminderFields,
    append: appendReminder,
    remove: removeReminder,
    update: updateReminder,
  } = useFieldArray({ control, name: 'reminders' });

  const watchTimers = useWatch({ control, name: 'reminders' });
  const rootErrorMessage = errors.reminders?.root?.message;
  const isHalfFilled = (timer?: FormReminder) => !timer?.reminder_seconds !== !timer?.node;
  // A list-level error with no half-filled row to point at shows under the list
  const showListError = !!rootErrorMessage && !watchTimers.some(isHalfFilled);

  const canAddTimer =
    watchTimers.length < REMINDER_CONSTRAINTS.MAX_REMINDERS &&
    watchTimers.every((timer) => timer.reminder_seconds && timer.node);

  const finalOptions: AutocompleteOption<NodeOption>[] = detailedOptions.map((option) => ({
    value: option.value,
    label: option.label,
    category: option.dialog_name,
    data: option,
  }));

  const renderNode = (option: AutocompleteOption<NodeOption>, side: 'top' | 'right') => (
    <NodeOptionLabel label={option.label} nodeType={option.data?.node_type} side={side} />
  );

  // Handle selection of an existing dialog node
  const handleAutoCompleteChange = (index: number, selectedValue: string) => {
    const data = detailedOptions.find((option) => option.value === selectedValue);

    if (data) {
      updateReminder(index, { ...watchTimers[index], node: data });
      // Field-array updates skip re-validation, so clear a shown error once the row is complete
      if (isSubmitted) {
        trigger('reminders');
      }
    }
  };

  // Handle "Create New" (Create Sample Dialog) - triggered via onCreateNew
  const handleCreateNew = (index: number, dialogName: string) => {
    // Reset node first
    updateReminder(index, { ...watchTimers[index], node: null });

    // Suggest a name based on timer if no name provided
    const suggestedName =
      dialogName ||
      (watchTimers[index].reminder_seconds
        ? `${t('ai_agents.reminder.title')} ${formatSecondsToReadable(watchTimers[index].reminder_seconds)}`
        : '');

    const modalProps = {
      dialogName: suggestedName,
      handleSubmit: (formData: CreateSampleDialogFormValues) => {
        createDialog(
          {
            dialog: {
              name: formData.dialogName,
              collection: 'Reminders',
              nodes: [
                {
                  name: formData.dialogName,
                  type: 'event',
                  enhance: languageSupportsEnhance(brain?.language, accountId),
                  actions: [{ type: 'text', texts: [formData.textResponse], action_id: uuidv4() }],
                  node_id: uuidv4(),
                  requisites: [],
                },
              ],
            },
            shouldNavigate: false,
          },
          {
            onSuccess: (dialog) => {
              dispatch(popModal());
              updateReminder(index, {
                ...watchTimers[index],
                node: {
                  dialog_name: dialog.name,
                  dialog_id: dialog.dialog_id,
                  value: dialog.nodes[0].node_id,
                  label: dialog.nodes[0].name,
                  node_type: 'event',
                },
              });
            },
          }
        );
      },
    };

    dispatch(pushModal(MODAL_CREATE_SAMPLE_DIALOG, modalProps));
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldHeader
        title={t('ai_agents.reminder.title')}
        tooltip={t('ai_agents.reminder.title_tooltip')}
      />

      {/* Reminder Fields */}
      <div className="flex flex-col gap-2">
        {reminderFields.map((field, index) => {
          const timer = watchTimers[index];
          const isEmpty = !timer || isEmptyReminder(timer);
          const showRemoveButton = !(reminderFields.length === 1 && isEmpty);
          const showPreviewButton = !!timer?.node?.dialog_id;
          const showRowError = !!rootErrorMessage && isHalfFilled(timer);

          return (
            <div key={field.id} className="flex flex-col gap-2">
              <div className="flex items-start gap-2">
                {/* Time Selector */}
                <div className="min-w-0 flex-1">
                  <Controller
                    name={`reminders.${index}.reminder_seconds`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <ReminderTimeSelect
                        value={field.value}
                        onChange={(value) => {
                          // Convert string to number, or null if empty string
                          field.onChange(value ? parseInt(value, 10) : null);
                        }}
                        disabled={!canWrite}
                        hasError={!!fieldState.error || (!field.value && showRowError)}
                        placeholder={t('ai_agents.reminder.select_duration')}
                      />
                    )}
                  />
                </div>

                {/* Node Selector */}
                <div className="min-w-0 flex-1">
                  <Autocomplete<NodeOption>
                    options={finalOptions}
                    value={watchTimers[index]?.node?.value ?? ''}
                    onChange={(selectedValue) => handleAutoCompleteChange(index, selectedValue)}
                    onCreateNew={(dialogName) => handleCreateNew(index, dialogName)}
                    placeholder={t('dialog.jump_to.placeholder')}
                    disabled={!canWrite}
                    error={!timer?.node && showRowError}
                    renderOption={(option) => renderNode(option, 'right')}
                    renderValue={(option) => renderNode(option, 'top')}
                  />
                </div>
                <div className="flex w-22 shrink-0 gap-2">
                  {/* Delete Button */}
                  {showRemoveButton && (
                    <InfoTooltip
                      content={t('ai_agents.reminder.delete_tooltip')}
                      side="top"
                      className="text-center"
                    >
                      <Button
                        type="button"
                        variant="outline-neutral"
                        size="icon-lg"
                        aria-label={t('ai_agents.reminder.delete_tooltip')}
                        className="text-muted-foreground hover:text-destructive hover:[&_svg]:text-destructive"
                        onClick={async () => {
                          // If there is only one timer, reset the form to empty state
                          if (reminderFields.length === 1) {
                            updateReminder(0, DEFAULT_TIMER_VALUES);
                            return;
                          }
                          removeReminder(index);
                          if (isSubmitted) {
                            await trigger('reminders');
                          }
                        }}
                        disabled={!canWrite}
                      >
                        <Trash2Icon size={16} />
                      </Button>
                    </InfoTooltip>
                  )}

                  {/* Preview Button */}
                  {showPreviewButton && (
                    <InfoTooltip content={t('ai_agents.reminder.preview_tooltip')} side="top">
                      <Button
                        type="button"
                        variant="outline-neutral"
                        size="icon-lg"
                        aria-label={t('ai_agents.reminder.preview_tooltip')}
                        onClick={() => {
                          dispatch(
                            pushModal(MODAL_DIALOG_PREVIEW, {
                              name: watchTimers[index].node.dialog_name,
                              dialogId: watchTimers[index].node.dialog_id,
                            })
                          );
                        }}
                      >
                        <EyeIcon size={16} />
                      </Button>
                    </InfoTooltip>
                  )}
                </div>
              </div>

              {showRowError && (
                <p role="alert" className="text-sm leading-5 text-warning">
                  {rootErrorMessage}
                </p>
              )}

              {/* Helper Text */}
              {!!watchTimers[index]?.reminder_seconds && !!watchTimers[index]?.node && (
                <span className="text-sm leading-5 font-normal text-muted-foreground">
                  <Trans
                    i18nKey="ai_agents.reminder.helper_text"
                    values={{
                      0: formatSecondsToReadable(watchTimers[index].reminder_seconds),
                      1: watchTimers[index].node.dialog_name,
                    }}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>

      {showListError && (
        <p role="alert" className="text-sm leading-5 text-warning">
          {rootErrorMessage}
        </p>
      )}

      {/* Add Another Button */}
      {canAddTimer && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 w-fit"
          disabled={!canWrite}
          onClick={() => {
            appendReminder({ reminder_id: uuidv4(), reminder_seconds: null, node: null });
          }}
        >
          <PlusIcon />
          {t('ai_agents.reminder.add_another')}
        </Button>
      )}
    </div>
  );
};
